import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { IntegerInput } from "shared/UI/Inputs/IntegerInput/IntegerInput";
import { Select } from "shared/UI/Select/Select";
import { ValueCallback } from "shared/core/types/callbacks";

export enum ActivityType {
  physical,
  mental,
}

export type ActivityEntry = {
  name: string;
  duration: number;
};

export const GeneralActivitySelector = ({
  activityEntry,
  onActivityChanged,
}: {
  activityEntry: ActivityEntry;
  onActivityChanged: ValueCallback<ActivityEntry>;
}) => {
  const { t } = useTranslation();
  const activities = useMemo(() => {
    const physicalActivities = t("wellbeing.trackers.activity.addData.physicalActivities", { returnObjects: true });
    const mentalActivities = t("wellbeing.trackers.activity.addData.mentalActivities", { returnObjects: true });

    return {
      [ActivityType.physical]: Object.values(physicalActivities),
      [ActivityType.mental]: Object.values(mentalActivities),
    };
  }, [t]);

  return (
    <div className="flex flex-row w-full gap-x-[4px] mb-1">
      <Select
        value={activityEntry.name}
        label={t("wellbeing.trackers.activity.addData.activityLabel")}
        onChange={(event) => onActivityChanged({ name: event.target.value, duration: activityEntry.duration })}
        placeholder={t("wellbeing.trackers.activity.addData.activityPlaceholder")}
      >
        <optgroup label={t("wellbeing.trackers.activity.addData.mentalActivityType")}>
          {activities[ActivityType.mental].map((activity, index) => (
            <option key={`mental-${activity}-${index}`} value={activity}>
              {activity}
            </option>
          ))}
        </optgroup>
        <optgroup label={t("wellbeing.trackers.activity.addData.physicalActivityType")}>
          {activities[ActivityType.physical].map((activity, index) => (
            <option key={`physical-${activity}-${index}`} value={activity}>
              {activity}
            </option>
          ))}
        </optgroup>
      </Select>
      <IntegerInput
        maxLength={3}
        label={t("wellbeing.trackers.activity.addData.durationLabel")}
        suffix={t("wellbeing.trackers.activity.addData.minutes")}
        defaultValue={activityEntry.duration.toString()}
        onChange={(e) => {
          onActivityChanged({ name: activityEntry.name, duration: Number(e.target.value) });
        }}
      />
    </div>
  );
};
